import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getApi } from '../../providers/AuthContext';
import PageWrapper from '../../components/PageWrapper';
import { defaultImage } from '../../settings/config';
import { Pencil, ArrowLeft, ShoppingCart, AlertCircle, User, Layout, Calendar } from 'lucide-react';

const SkeletonCard = () => (
  <div className="max-w-3xl mx-auto">
    <div className="rounded-2xl overflow-hidden bg-card border border-border/40">
      <div className="h-1.5 bg-gradient-to-r from-muted via-muted-foreground/10 to-muted" />
      <div className="flex flex-col md:flex-row">
        <div className="skeleton h-64 md:h-auto md:w-80 w-full rounded-none shrink-0" />
        <div className="flex-1 p-6 space-y-4">
          <div className="skeleton h-8 w-48 rounded-lg" />
          <div className="flex gap-2">
            <div className="skeleton h-6 w-24 rounded-full" />
            <div className="skeleton h-6 w-20 rounded-full" />
          </div>
          <div className="skeleton h-4 w-full rounded-lg" />
          <div className="flex gap-3 mt-6">
            <div className="skeleton h-10 w-28 rounded-xl" />
            <div className="skeleton h-10 w-20 rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const api = getApi();
  const [card, setCard] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCard = async () => {
      try {
        setError(null);
        const result = await api.get(`/api/v1/cards/${id}`);
        setCard(result.card);
      } catch (err) {
        setError('Failed to load card.');
      } finally {
        setLoading(false);
      }
    };
    loadCard();
  }, [id]);

  return (
    <PageWrapper pageTitle={card ? card.title : 'Card'}>
      <div className="p-4 lg:p-6">
        {loading ? (
          <SkeletonCard />
        ) : error ? (
          <div className="max-w-3xl mx-auto">
            <div className="p-4 bg-destructive/10 dark:bg-destructive/20 text-destructive border border-destructive/20 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span className="flex-1 text-sm font-medium">{error}</span>
              <Link to="/cards" className="text-sm underline text-destructive hover:text-destructive/80 transition-colors">Go Back</Link>
            </div>
          </div>
        ) : card ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-3xl mx-auto"
          >
            <div className="rounded-2xl overflow-hidden bg-card border border-border/40 shadow-soft-md">
              {/* Accent bar */}
              <div className="h-1.5 bg-gradient-to-r from-accent via-primary to-secondary" />

              {/* Card layout: image + details side-by-side on desktop */}
              <div className="flex flex-col md:flex-row">
                {/* Card preview image */}
                <div className="relative md:w-80 shrink-0">
                  <img
                    alt={card.title}
                    src={card.preview_image || defaultImage}
                    className="w-full h-64 md:h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-black/10" />

                  {/* Purchase badge on image */}
                  {card.purchase_url && (
                    <a
                      href={card.purchase_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-success/90 text-white text-sm font-medium backdrop-blur-sm hover:bg-success transition-colors no-underline shadow-soft-sm"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Purchase
                    </a>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 p-6 sm:p-8">
                  <h2 className="font-bold text-2xl sm:text-3xl text-card-foreground mb-3">
                    {card.title}
                  </h2>

                  {/* Metadata chips */}
                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    {card.child_name && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-primary/10 dark:bg-primary/15 text-primary text-xs font-medium">
                        <User className="w-3 h-3" />
                        {card.child_name}
                      </span>
                    )}
                    {card.template_name && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-accent/10 dark:bg-accent/15 text-accent text-xs font-medium">
                        <Layout className="w-3 h-3" />
                        {card.template_name}
                      </span>
                    )}
                    {card.created_at && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-muted text-muted-foreground text-xs font-medium">
                        <Calendar className="w-3 h-3" />
                        {new Date(card.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                  </div>

                  {/* Purchase CTA if not on badge */}
                  {card.purchase_url && (
                    <a
                      href={card.purchase_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-success/10 dark:bg-success/15 text-success text-sm font-medium hover:bg-success/20 transition-colors no-underline mb-5"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Purchase this card
                    </a>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 mt-6">
                    <Link
                      to={`/cards/${id}/edit`}
                      className="btn-tertiary py-2.5 px-5 rounded-xl no-underline gap-2"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit Card
                    </Link>
                    <Link
                      to="/cards"
                      className="btn-light py-2.5 px-5 rounded-xl no-underline text-inherit gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <p className="text-muted-foreground mb-4">Card not found.</p>
            <Link to="/cards" className="btn-light py-2.5 px-5 rounded-xl no-underline text-inherit gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Cards
            </Link>
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export default Page;
